import { useCallback } from 'react';
import { useProductTagging } from '../../context';
import type { TagDataUseToCreateTag } from '@modules/product-tagging/type';
import TaggingModalForm from '@modules/product-tagging/components/tagging-modal-form/TaggingModalForm';
import type { TaggingFormFieldDescriptor } from '@modules/product-tagging/components/tagging-modal-form/TaggingModalForm';

const FIELDS: TaggingFormFieldDescriptor[] = [
    {
        name: 'companyName',
        placeholder: 'Company or brand name',
        maxLength: 255,
        autoFocus: true,
    },
    {
        name: 'productName',
        placeholder: 'Product name',
        maxLength: 255,
    },
    {
        name: 'productURL',
        placeholder: 'https://supplier.com/product-page',
        maxLength: 255,
        isURL: true,
    },
];

interface SupplierDetailsFormProps {
    onSubmit: (data: TagDataUseToCreateTag) => void;
    onDismiss?: () => void;
}

/**
 * Prospecting flow: supplier not on ArchiPro. Collects company name, product name, and a URL
 * (product page or site) stored on LibraryImageMarker as TitleForUnknownProfessional,
 * TitleForUnknownProduct, and URLForUnknownProduct.
 */
const SupplierDetailsForm = ({
    onSubmit,
    onDismiss: onDismissOverride,
}: SupplierDetailsFormProps) => {
    const { dispatch } = useProductTagging();

    const defaultDismiss = useCallback(
        () => dispatch({ type: 'ADDING_TAG', payload: undefined }),
        [dispatch],
    );

    const dismiss = onDismissOverride ?? defaultDismiss;

    const buildPayload = useCallback(
        (values: Record<string, string>): TagDataUseToCreateTag => ({
            titleForUnknownProfessional: values.companyName,
            titleForUnknownProduct: values.productName,
            urlForUnknownProduct: values.productURL,
        }),
        [],
    );

    return (
        <TaggingModalForm
            title="Add supplier details"
            message={
                <>
                    This supplier isn&apos;t on ArchiPro yet. Add the company
                    name, the product name, and a link to the product page on
                    their website so we can follow up.
                </>
            }
            fields={FIELDS}
            buildPayload={buildPayload}
            onSubmit={onSubmit}
            onDismiss={dismiss}
            formId="add-supplier-form"
        />
    );
};

export default SupplierDetailsForm;
